// src/components/health/dashboard/RecentHealthActivity.tsx
// Recent Health Activity Feed Component

import React from 'react';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow, parseISO, isToday, isYesterday } from 'date-fns';
import { 
  Zap, 
  Pill, 
  Heart, 
  Thermometer, 
  Moon,
  Activity,
  Clock,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HealthLog } from '@/features/health/types/healthTypes/healthTypes';
import type { MedicationReminder } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface RecentHealthActivityProps {
  logs: HealthLog[];
  medications: MedicationReminder[];
  onViewAll: () => void;
  maxItems?: number;
}

// ============================================================================
// Component
// ============================================================================

export const RecentHealthActivity: React.FC<RecentHealthActivityProps> = ({
  logs,
  medications,
  onViewAll,
  maxItems = 5,
}) => {
  // Combine and sort activities
  const activities = combineActivities(logs, medications).slice(0, maxItems);

  return (
    <div className="bg-white rounded-xl border border-cream-dark p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-sage-dark" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy">Recent Activity</h3>
            <p className="text-sm text-warm-gray">Latest health entries and medications</p>
          </div>
        </div>
        <button
          onClick={onViewAll}
          className="text-sm font-medium text-sage-dark hover:text-sage flex items-center gap-1"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Activity List */}
      {activities.length === 0 ? (
        <EmptyActivityState />
      ) : (
        <div className="space-y-1">
          {activities.map((activity, index) => (
            <ActivityItem key={activity.id} activity={activity} index={index} />
          ))}
        </div>
      )}

      {/* Pending Medications */}
      {medications.filter(m => m.status === 'pending').length > 0 && (
        <div className="mt-6 pt-6 border-t border-cream-dark">
          <h4 className="text-sm font-semibold text-navy mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-terracotta" />
            Upcoming Medications
          </h4>
          <div className="space-y-2">
            {medications
              .filter(m => m.status === 'pending')
              .slice(0, 3)
              .map(med => (
                <MedicationReminderItem key={med.id} reminder={med} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Activity Item Component
// ============================================================================

interface ActivityItemData {
  id: string;
  type: 'health_log' | 'medication';
  category: string;
  title: string;
  description?: string;
  timestamp: Date;
  severity?: number;
  icon: React.FC<{ className?: string }>;
  color: string;
}

interface ActivityItemProps {
  activity: ActivityItemData;
  index: number;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity, index }) => {
  const IconComponent = activity.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="
        flex items-start gap-4 p-3 rounded-lg
        hover:bg-cream/50 transition-colors duration-200
        group cursor-pointer
      "
    >
      {/* Icon */}
      <div 
        className={`
          flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center
          ${activity.color}
        `}
      >
        <IconComponent className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium text-navy text-sm line-clamp-1">
              {activity.title}
            </p>
            {activity.description && (
              <p className="text-sm text-warm-gray mt-0.5 line-clamp-1">
                {activity.description}
              </p>
            )}
          </div>
          
          {/* Severity Badge */}
          {activity.severity && activity.severity >= 7 && (
            <span className="
              flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full
              bg-terracotta/10 text-terracotta
            ">
              High
            </span>
          )}
        </div>

        {/* Timestamp */}
        <p className="text-xs text-warm-gray mt-1">
          {formatActivityTime(activity.timestamp)}
        </p>
      </div>

      {/* Arrow */}
      <ChevronRight className="
        w-4 h-4 text-warm-gray opacity-0 group-hover:opacity-100
        transition-opacity duration-200 flex-shrink-0
      " />
    </motion.div>
  );
};

// ============================================================================
// Medication Reminder Item
// ============================================================================

interface MedicationReminderItemProps {
  reminder: MedicationReminder;
}

const MedicationReminderItem: React.FC<MedicationReminderItemProps> = ({ reminder }) => {
  const isPast = new Date(reminder.scheduledTime) < new Date();
  
  return (
    <div className={`
      flex items-center gap-3 p-3 rounded-lg
      ${isPast ? 'bg-terracotta/5 border border-terracotta/20' : 'bg-cream'}
    `}>
      <div className={`
        w-8 h-8 rounded-lg flex items-center justify-center
        ${isPast ? 'bg-terracotta/10' : 'bg-sage/10'}
      `}>
        <Pill className={`w-4 h-4 ${isPast ? 'text-terracotta' : 'text-sage-dark'}`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-navy text-sm">{reminder.medicationName}</p>
        <p className="text-xs text-warm-gray">{reminder.dosage}</p>
      </div>

      <div className="text-right">
        <p className={`text-sm font-medium ${isPast ? 'text-terracotta' : 'text-navy'}`}>
          {format(new Date(reminder.scheduledTime), 'h:mm a')}
        </p>
        {isPast && (
          <p className="text-xs text-terracotta">Overdue</p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-1">
        <button
          className="p-1.5 rounded-lg hover:bg-success/10 transition-colors"
          title="Mark as taken"
        >
          <CheckCircle2 className="w-4 h-4 text-success" />
        </button>
        <button
          className="p-1.5 rounded-lg hover:bg-terracotta/10 transition-colors"
          title="Skip"
        >
          <AlertCircle className="w-4 h-4 text-warm-gray" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Empty State
// ============================================================================

const EmptyActivityState: React.FC = () => (
  <div className="py-8 text-center">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cream flex items-center justify-center">
      <Activity className="w-8 h-8 text-warm-gray" />
    </div>
    <p className="font-medium text-navy">No recent activity</p>
    <p className="text-sm text-warm-gray mt-1">
      Health entries will appear here as they're logged.
    </p>
  </div>
);

// ============================================================================
// Helper Functions
// ============================================================================

function combineActivities(
  logs: HealthLog[],
  medications: MedicationReminder[]
): ActivityItemData[] {
  const activities: ActivityItemData[] = [];

  // Add health logs
  logs.forEach(log => {
    activities.push({
      id: log.id,
      type: 'health_log',
      category: log.logType?.slug || 'other',
      title: log.logType?.name || 'Health Entry',
      description: log.notes || undefined,
      timestamp: log.occurredAt ? new Date(log.occurredAt) : new Date(),
      severity: log.severity || undefined,
      icon: getLogIcon(log.logType?.slug),
      color: getLogColor(log.logType?.slug),
    });
  });

  // Add completed medications
  medications
    .filter(m => m.status === 'taken' || m.status === 'missed')
    .forEach(med => {
      activities.push({
        id: med.id,
        type: 'medication',
        category: 'medication',
        title: med.medicationName,
        description: `${med.dosage} - ${med.status === 'taken' ? 'Taken' : 'Missed'}`,
        timestamp: new Date(med.scheduledTime),
        icon: Pill,
        color: med.status === 'taken' ? 'bg-success/10 text-success' : 'bg-terracotta/10 text-terracotta',
      });
    });

  // Sort by timestamp (newest first)
  return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

function getLogIcon(slug?: string): React.FC<{ className?: string }> {
  const icons: Record<string, React.FC<{ className?: string }>> = {
    seizure: Zap,
    medication: Pill,
    therapy: Heart,
    symptom: Thermometer,
    sleep: Moon,
    vital: Activity,
  };
  return icons[slug || ''] || Activity;
}

function getLogColor(slug?: string): string {
  const colors: Record<string, string> = {
    seizure: 'bg-terracotta/10 text-terracotta',
    medication: 'bg-sage/10 text-sage-dark',
    therapy: 'bg-sage/10 text-sage-dark',
    symptom: 'bg-terracotta/10 text-terracotta',
    sleep: 'bg-navy/10 text-navy',
    vital: 'bg-blue-100 text-blue-600',
  };
  return colors[slug || ''] || 'bg-cream text-warm-gray';
}

function formatActivityTime(date: Date): string {
  if (isToday(date)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  if (isYesterday(date)) {
    return `Yesterday at ${format(date, 'h:mm a')}`;
  }
  return format(date, 'MMM d, h:mm a');
}

export default RecentHealthActivity;

